import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import {
    documentApi,
    useGetCapitalCallsQuery,
    useGetClosingsQuery,
    useGetShareSubcategoriesQuery,
    useReopenSubscriptionMutation,
} from 'api/main';
import { userSelectors } from 'reducers';
import { notificationCreated } from 'reducers/appNotification';
import {
    downloadClick,
    getFilenameFromHeaders,
    getLatestClosing,
    getShareSubcategoryIdFromIRI,
    parseFullName,
    parseSum,
} from 'utils/helpers';
import { useDialog, useMatchAtLeastOneRole, useUserRoleMatchCategory } from 'utils/hooks';
import { RoleNames, roleCategories, environments } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import {
    MenuDetails,
    MenuDownload,
    MenuNewIndirectSubscription,
    MenuPortfolio,
    MenuReopen,
    MenuSendMessage,
    MenuSendSMS,
    MenuSubscriptionMove,
    MenuSubstitute,
} from 'components/ActionsMenu/actions';
import SubscriptionInvite from 'containers/subscriptions/SubscriptionInvite';
import { ActionsMenu, ConfirmDialog, SMSDialog, StyledTableRow } from 'components';
import { TableCell, Typography } from '@mui/material';

const ImpersonateItem = React.forwardRef((props, ref) => <MenuSubstitute ref={ref} {...props} />);

const InvestorsRow = ({ investor: { availabilityStatus: status, ...subscription } }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const internalUser = useSelector(userSelectors.getInternalUser);

    const { data: shareSubcategories = [] } = useGetShareSubcategoriesQuery();

    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);
    const userIsSignatoryServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_SIGNATORY]);

    const isAdminOrSignatory = useMatchAtLeastOneRole([
        RoleNames.SERVICER_SIGNATORY,
        RoleNames.SERVICER_ADMIN,
    ]);
    const userIsAdminDistributor = useMatchAtLeastOneRole([RoleNames.ADMIN_CIF, RoleNames.PSI]);
    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
        RoleNames.PSI,
    ]);
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);

    const [downloading, setDownloading] = useState(false);
    const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
    const [reopenId, setReopenId] = useState(null);
    const [smsOpen, setSmsOpen] = useState(false);
    const [newSubscriptionOpen, setNewSubscriptionOpen] = useState(false);
    const {
        open: alertTunnelChangesOpen,
        onClose: handleAlertTunnelChangesClose,
        onOpen: handleAlertTunnelChangesOpen,
    } = useDialog();

    const [reopenSubscription, { isSuccess: reopenSuccess, isLoading: reopenLoader }] =
        useReopenSubscriptionMutation();
    const { data: capitalCalls = [] } = useGetCapitalCallsQuery(
        { investmentFund: subscription.investmentFund?.id, status: ['completed', 'activated'] },
        { skip: !subscription.investmentFund?.id || !isAdminOrSignatory }
    );
    const { data: closings = [] } = useGetClosingsQuery(
        {
            pagination: false,
            investmentFund: subscription.investmentFund?.id,
            status: ['completed', 'activated'],
        },
        { skip: !subscription.investmentFund?.id || !isAdminOrSignatory }
    );

    const distributorCompany = useMemo(
        () => internalUser?.subscriptions?.[0]?.distributorCompany,
        [internalUser]
    );
    const latestCapitalDate = useMemo(() => {
        const latestCapitalCallDate = getLatestClosing([...capitalCalls])?.issueDate;
        const latestClosingDate = getLatestClosing([...closings])?.issueDate;
        if (!latestCapitalCallDate && !latestClosingDate) return null;
        if (latestCapitalCallDate && latestClosingDate) {
            return moment(latestCapitalCallDate).isAfter(latestClosingDate, 'd')
                ? moment(latestCapitalCallDate)
                : moment(latestClosingDate);
        }
        return latestCapitalCallDate ? moment(latestCapitalCallDate) : moment(latestClosingDate);
    }, [closings, capitalCalls]);

    const onSendSMS = useCallback(() => {
        setSmsOpen(true);
    }, []);

    const handleSubscriptionInvite = useCallback(() => {
        setNewSubscriptionOpen(true);
    }, []);
    const handleStartNewIndirectSubscription = useCallback(
        user => () => {
            handleAlertTunnelChangesClose();
            navigate(`/add-indirect-subscription?userId=${user.id}`);
        },
        [navigate, handleAlertTunnelChangesClose]
    );

    const downloadPEC = useCallback(
        (fundId, userId) => async () => {
            try {
                setDownloading(true);
                const response = await documentApi.generatePEC({ userIds: [userId], fundId });
                const PECFileName = getFilenameFromHeaders(response.headers) || 'PEC-file';
                const bytes = new Uint8Array(response.data);
                const blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const url = window.URL.createObjectURL(blob);
                downloadClick(url, PECFileName, false);
                setDownloading(false);
            } catch (error) {
                if (process.env.REACT_APP_ENV !== environments.PROD) {
                    console.log(error);
                }
                dispatch(notificationCreated('FAILED_DOCUMENT_DOWNLOAD'));
                setDownloading(false);
            }
        },
        [dispatch]
    );

    const handleConfirmReopen = useCallback(() => {
        reopenSubscription(reopenId);
    }, [reopenSubscription, reopenId]);

    const subscriptionFinishedAt = subscription.finishedSubscriptionAt
        ? moment(subscription.finishedSubscriptionAt).format('L')
        : null;
    const profile = subscription.role?.name?.includes('PP') ? 'PP' : 'PM';

    const shareSubCategoryId = getShareSubcategoryIdFromIRI(
        subscription.investmentCharacteristics?.shareSubcategory || '-1'
    );
    const shareSubcategory = shareSubcategories?.find(
        subCategory => subCategory.id === shareSubCategoryId
    );
    const admissionFee = subscription?.investmentCharacteristics?.admissionFee;
    const canStartNewIndirectSubscription =
        ((userIsDistributor &&
            distributorCompany?.id === subscription.distributorCompany?.id &&
            !internalUser?.impersonatedByUser) ||
            (!userIsDistributor && !subscription.distributorCompany)) &&
        !subscription.ongoingSubscription &&
        !userIsProvider &&
        !userIsPromoter &&
        !userIsNetworkAdmin;

    const canReopenTunnel = useMemo(() => {
        return (
            !subscription.ongoingSubscription &&
            isAdminOrSignatory &&
            (!latestCapitalDate ||
                moment(subscription.finishedSubscriptionAt).isAfter(latestCapitalDate, 'd'))
        );
    }, [subscription, isAdminOrSignatory, latestCapitalDate]);

    useEffect(() => {
        if (reopenSuccess) {
            setReopenDialogOpen(false);
        }
    }, [reopenSuccess]);

    return (
        <StyledTableRow key={subscription.id}>
            <TableCell sx={theme => ({ color: theme.palette.common.black })}>
                {profile === 'PP' ? (
                    <React.Fragment>
                        <Typography gutterBottom fontSize="inherit">
                            {parseFullName(subscription.user)}
                        </Typography>
                        {subscription.coSubscriber ? (
                            <Typography fontSize="inherit">
                                {parseFullName(subscription.coSubscriber)}
                            </Typography>
                        ) : (
                            ''
                        )}
                    </React.Fragment>
                ) : (
                    subscription.user?.investorCompany?.corporateName
                )}
            </TableCell>
            <TableCell>{subscription.investmentFund?.name || ''}</TableCell>
            <TableCell align="center">{profile}</TableCell>
            <TableCell align="center">{shareSubcategory?.name || ''}</TableCell>
            <TableCell sx={theme => ({ fontWeight: 600, color: theme.palette.common.black })}>
                {parseSum(subscription.investmentCharacteristics?.investingSum)}
            </TableCell>
            <TableCell>{admissionFee === null ? '-' : `${admissionFee}\xa0%`}</TableCell>
            <TableCell>{subscriptionFinishedAt || ''}</TableCell>
            {!userIsDistributor && (
                <TableCell>
                    {subscription.distributorCompany?.corporateName ||
                        subscription.investmentFund?.sgp?.name}
                </TableCell>
            )}
            {userIsDistributor && (
                <TableCell>
                    {subscription.createdBy ? parseFullName(subscription.createdBy) : '-'}
                </TableCell>
            )}
            <TableCell>
                <ActionsMenu autoClose>
                    {!userIsNetworkAdmin &&
                        !userIsProvider &&
                        !userIsPromoter &&
                        !userIsDistributor &&
                        !userIsStandardServicer && (
                            <ImpersonateItem
                                user={subscription.user}
                                originalPath="/investors"
                                connectAs="subscriber"
                                itemText={texts.IMPERSONATE_SUBSCRIBER}
                            />
                        )}
                    {canStartNewIndirectSubscription && (
                        <MenuNewIndirectSubscription
                            onClick={
                                userIsDistributor
                                    ? handleAlertTunnelChangesOpen
                                    : handleSubscriptionInvite
                            }
                        />
                    )}
                    {!userIsNetworkAdmin &&
                        !userIsProvider &&
                        !userIsStandardServicer &&
                        !userIsPromoter && <MenuSendMessage receiver={subscription.user} />}
                    {!userIsNetworkAdmin &&
                        !userIsProvider &&
                        !userIsStandardServicer &&
                        !userIsPromoter && (
                            <MenuSendSMS onSendSMS={onSendSMS} id={subscription.user.id} />
                        )}
                    <MenuDetails path={`details/${subscription.user.id}/${subscription.id}`} />
                    {canReopenTunnel && (
                        <MenuReopen
                            onClick={() => {
                                setReopenId(subscription.id);
                                setReopenDialogOpen(true);
                            }}
                        />
                    )}
                    {!userIsNetworkAdmin &&
                        !userIsProvider &&
                        !userIsAdminDistributor &&
                        !userIsSignatoryServicer &&
                        !userIsStandardServicer &&
                        !userIsPromoter && (
                            <MenuDownload
                                downloading={downloading}
                                download={downloadPEC(
                                    subscription.investmentFund?.id,
                                    subscription.user.id
                                )}
                            />
                        )}
                    {userIsAdminDistributor && (
                        <MenuSubscriptionMove subscriptionId={subscription.id} />
                    )}
                    {userIsDistributor && <MenuPortfolio userId={subscription.user.id} />}
                </ActionsMenu>
            </TableCell>
            <ConfirmDialog
                loading={reopenLoader}
                open={reopenDialogOpen}
                title={texts.REOPEN_TUNNEL}
                handleClose={() => setReopenDialogOpen(false)}
                handleConfirm={handleConfirmReopen}
            >
                {texts.CONFIRM_REOPEN_TUNNEL}
            </ConfirmDialog>
            <SMSDialog
                open={smsOpen}
                handleClose={() => setSmsOpen(false)}
                receiver={subscription.user}
            />
            {canStartNewIndirectSubscription && !userIsDistributor && (
                <SubscriptionInvite
                    key={subscription?.user?.id}
                    open={newSubscriptionOpen}
                    user={subscription?.user}
                    handleToggle={() => setNewSubscriptionOpen(prevState => !prevState)}
                />
            )}
            {userIsDistributor && (
                <ConfirmDialog
                    open={alertTunnelChangesOpen}
                    handleConfirm={handleStartNewIndirectSubscription(subscription?.user)}
                    handleClose={handleStartNewIndirectSubscription(subscription?.user)}
                    title={texts.TUNNEL_CHANGES_ALERT_TITLE}
                    confirmText={texts.CLOSE}
                    hideCancelButton
                >
                    {texts.TUNNEL_CHANGES_ALERT_CONTENT.map(content => (
                        <Typography key={content} variant="body2" gutterBottom>
                            {content}
                        </Typography>
                    ))}
                </ConfirmDialog>
            )}
        </StyledTableRow>
    );
};

InvestorsRow.propTypes = {
    investor: PropTypes.object.isRequired,
};

export default InvestorsRow;
